import { trackEvent } from "../utils/track-event";
import type { TrackingEvent, TrackingEventResponse } from "../utils/types";
import type { SingleProduct, ProductVariation } from "src/types/graphql.d";

type ProductViewedEventProps = {
  product: SingleProduct;
  isLoggedIn?: boolean;
  email?: string | null;
  resultId?: string;
};

type ProductViewedAnalyticsProps = {
  brand: string;
  brand_id: string;
  category: unknown;
  category_id: unknown;
  currency?: string;
  image_url?: string | null;
  labels: string[];
  name: string;
  price: number;
  product_discount?: number | null;
  product_id: string;
  product_kids_gender?: unknown;
  product_shop_type?: unknown;
  regular_price: number;
  sizes_available?: unknown;
  url: string;
  gender?: string | null;
  product_variation?: string | null;
  gate_level: unknown;
  gated: boolean;
  email?: string | null;
  result_id?: string;
};

type ProductViewedDataLayerProps = {
  event: "view_item";
  items: {
    item_id: string;
    item_sku?: string | null;
    item_name: string;
    item_variant_sku?: string | null;
    item_variant_name?: string | null;
    brand_id: string;
    brand_name: string;
    item_quantity: number;
    item_category: { level: string; name: string; id: string }[];
    item_availability?: boolean;
    price: number;
    retail_price: number;
    discount_value?: number | null;
    discount_type: string;
    item_custom_labels: { label_id: string; label_name: string }[];
  }[];
};

export type ProductViewedEvent = TrackingEvent<
  "Product Viewed",
  ProductViewedAnalyticsProps,
  ProductViewedDataLayerProps
>;

const getAvailableSizes = (variations?: ProductVariation[]) =>
  (variations ?? []).filter((v) => v.stock_quantity > 0).map((v) => v.size);

export async function trackProductViewedEvent({
  product,
  isLoggedIn,
  email,
  resultId,
}: ProductViewedEventProps): TrackingEventResponse {
  return await trackEvent("Product Viewed", {
    props: {
      brand: product.brand.name,
      brand_id: product.brand.id,
      category: product?.translated_category_names || [],
      category_id: product?.translated_category_ids || [],
      currency: product?.currency?.code,
      image_url: product.images?.[0],
      labels: (product.labels || []).map(({ value }) => value),
      name: product.name,
      price: product.price,
      product_discount: product.product_discount,
      product_id: product.id,
      product_kids_gender: product.product_shop_type?.includes("kids")
        ? product.product_kids_gender
        : undefined,
      product_shop_type: product.product_shop_type,
      regular_price: product.regular_price,
      sizes_available: product.variations
        ? getAvailableSizes(product.variations)
        : undefined,
      url: `product/${product.slug}`,
      gender: product.gender,
      product_variation: product.variations?.[0]?.id,
      gate_level: product.gate?.level || null,
      gated: !isLoggedIn,
      email,
      result_id: resultId,
    },
    dataLayerProps: {
      event: "view_item",
      items: [
        {
          item_id: product.id,
          item_sku: product.sku,
          item_name: product.name,
          item_variant_sku: product.variations?.[0]?.id,
          item_variant_name: product.variations?.[0]?.size,
          brand_id: product.brand.id,
          brand_name: product.brand.name,
          item_quantity: 1,
          item_category: product?.translated_category_ids?.length
            ? product.translated_category_ids.map((id, index) => ({
                level: (index + 1).toString(),
                name: product?.translated_category_names?.length
                  ? product?.translated_category_names[index] || ""
                  : "",
                id: id || "",
              }))
            : [],
          item_availability: product.variations?.some(
            (item) => item.stock_quantity > 1
          ),
          price: product.price,
          retail_price: product.regular_price,
          discount_value: product.product_discount,
          discount_type: "absolute",
          item_custom_labels: (product.labels || []).map(({ type, value }) => ({
            label_id: type,
            label_name: value,
          })),
        },
      ],
    },
    withRequestSource: true,
  });
}
